import React, { useState, useEffect } from 'react';
import apiClient, { organizationService } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import StatsCard from '../components/StatsCard';
import BalanceGraph from '../components/BalanceGraph';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Wallet, Target, Trophy, TrendingUp, XCircle, ArrowUpRight, ArrowDownRight, Clock, Award, Plus, Mail, ShieldCheck, ShieldAlert, Link as LinkIcon, Save, Loader2, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import ImageCropperModal from '../components/ImageCropperModal';

const badgeIcons = {
  Trophy: Trophy,
  Target: Target,
  Plus: Plus,
  Award: Award
};

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const { t, language } = useLanguage();
  const [bets, setBets] = useState([]);
  const [totalBetsCount, setTotalBetsCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [betsLoading, setBetsLoading] = useState(false);
  const betsLimit = 20;
  const [transactions, setTransactions] = useState([]);
  const [betStats, setBetStats] = useState({ total_profit: 0, total_loss: 0, total_pending: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paypalLink, setPaypalLink] = useState(user?.paypal_link || '');
  const [needsPaypal, setNeedsPaypal] = useState(false); // PayPal nur fuer Mitglieder einer Settlement-Org
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [localAvatarPreviewUrl, setLocalAvatarPreviewUrl] = useState('');
  const [selectedFileForCrop, setSelectedFileForCrop] = useState(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);

  useEffect(() => {
    fetchProfileData();
    // PayPal nur zeigen, wenn Mitglied einer Org mit aktivierter Abrechnung
    organizationService.getAll()
      .then(res => setNeedsPaypal((res.data || []).some(o => o.membership === 'member' && o.settlement_enabled)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (currentPage > 1) {
      fetchBets(currentPage);
    }
  }, [currentPage]);

  useEffect(() => {
    if (user) {
      setPaypalLink(user.paypal_link || '');
      setAvatarUrl(user.avatar_url || '');
      setLocalAvatarPreviewUrl('');
    }
  }, [user]);

  const fetchBets = async (page) => {
    setBetsLoading(true);
    try {
      const response = await apiClient.get(`/bets?page=${page}&limit=${betsLimit}`);
      setBets(Array.isArray(response.data?.bets) ? response.data.bets : []);
      setTotalBetsCount(response.data?.total_count || 0);
      setTotalPages(response.data?.total_pages || 1);
      setBetStats(response.data?.stats || { total_profit: 0, total_loss: 0, total_pending: 0 });
    } catch (error) {
      console.error('Failed to fetch bets:', error);
    } finally {
      setBetsLoading(false);
    }
  };

  const fetchProfileData = async () => {
    try {
      const [betsRes, transactionsRes] = await Promise.all([
        apiClient.get(`/bets?page=1&limit=${betsLimit}`),
        apiClient.get(`/transactions?limit=10000`),
      ]);
      setBets(Array.isArray(betsRes.data?.bets) ? betsRes.data.bets : []);
      setTotalBetsCount(betsRes.data?.total_count || 0);
      setTotalPages(betsRes.data?.total_pages || 1);
      setBetStats(betsRes.data?.stats || { total_profit: 0, total_loss: 0, total_pending: 0 });
      setTransactions(Array.isArray(transactionsRes.data) ? transactionsRes.data : []);
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error(language === 'de' ? 'Datei ist zu groß (max. 10MB)' : 'File is too large (max. 10MB)');
      return;
    }

    setSelectedFileForCrop(file);
    setIsCropperOpen(true);
    e.target.value = ''; // Reset so the same file can be uploaded again
  };

  const handleCroppedAvatarUpload = async (croppedBlob) => {
    const localUrl = URL.createObjectURL(croppedBlob);
    setLocalAvatarPreviewUrl(localUrl);

    const formData = new FormData();
    formData.append('file', croppedBlob, 'avatar.jpg');

    setUploadingAvatar(true);
    try {
      const response = await apiClient.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      const newAvatarUrl = response.data.url;
      setAvatarUrl(newAvatarUrl);
      
      // Auto-save to profile database
      await updateProfile({ avatar_url: newAvatarUrl });
      toast.success(language === 'de' ? 'Profilbild aktualisiert!' : 'Profile picture updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'de' ? 'Fehler beim Upload' : 'Upload failed'));
      setLocalAvatarPreviewUrl('');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({ paypal_link: paypalLink, avatar_url: avatarUrl });
      toast.success(language === 'de' ? 'Profil aktualisiert!' : 'Profile updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const safeBets = Array.isArray(bets) ? bets : [];
  const safeTransactions = Array.isArray(transactions) ? transactions : [];
  const userTotalBets = user?.total_bets || 0;
  const userWonBets = user?.won_bets || 0;
  const winRate = userTotalBets > 0 ? ((userWonBets / userTotalBets) * 100).toFixed(0) : 0;

  if (loading) {
    return (
      <div className="app-container" data-testid="profile-loading">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Skeleton className="h-10 w-48 mb-6" />
          <div className="stats-grid mb-6">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  return (
    <div className="app-container" data-testid="profile-page">

      <main className="max-w-4xl mx-auto px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left bg-secondary/10 p-4 rounded-2xl border border-border/20 backdrop-blur-md">
          <Avatar className="h-20 w-20 shadow-md border-2 border-primary/20">
            <AvatarImage src={user?.avatar_url} alt={user?.username} className="object-cover" />
            <AvatarFallback className="bg-red-600/20 text-red-500 font-bold text-2xl">
              {user?.username?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">{user?.username}</h1>
            <p className="text-muted-foreground mt-1 text-xs sm:text-sm">
              {t('memberSince')} {new Date(user?.created_at).toLocaleDateString(undefined)}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-6" data-testid="profile-stats">
          <StatsCard
            title={t('netProfitLoss')}
            value={(user?.total_profit ?? betStats.total_profit) - (user?.total_loss ?? betStats.total_loss)}
            icon={Wallet}
          />
          <StatsCard
            title={t('won')}
            value={Math.max(0, user?.total_profit ?? betStats.total_profit)}
            icon={Trophy}
            className="text-green-500"
          />
          <StatsCard
            title={t('lost')}
            value={user?.total_loss ?? betStats.total_loss}
            icon={XCircle}
            className="text-red-500"
          />
          <StatsCard
            title={t('activeWager')}
            value={user?.total_pending ?? betStats.total_pending}
            icon={Target}
          />
          <StatsCard
            title={t('totalBets')}
            value={totalBetsCount}
            icon={TrendingUp}
          />
          <StatsCard
            title={t('winRate')}
            value={`${winRate}%`}
            icon={Trophy}
          />
        </div>

        {/* Achievements */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-500" />
              {t('achievements')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {user?.badges && user.badges.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {user.badges.map(badge => {
                  const Icon = badgeIcons[badge.icon] || Award;
                  return (
                    <div key={badge.id} className="flex flex-col items-center p-3 rounded-lg bg-secondary/30 border border-border/50 text-center">
                      <div className="p-3 rounded-full bg-yellow-500/10 mb-2">
                        <Icon className="w-6 h-6 text-yellow-500" />
                      </div>
                      <p className="font-bold text-sm">{badge.name}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {new Date(badge.awarded_at).toLocaleDateString(undefined)}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t('noAchievements')}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Balance Graph */}
        {transactions.length > 0 && (
          <div className="bg-card border border-border/50 rounded-xl p-3 sm:p-6">
            <h3 className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">{t('balanceOverTime')}</h3>
            <div className="h-[200px] sm:h-[300px] w-full">
              <BalanceGraph transactions={transactions} bets={bets} />
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="bets" className="w-full">
          <TabsList className="grid w-full sm:max-w-xl grid-cols-3">
            <TabsTrigger value="bets" data-testid="tab-bets" className="text-xs sm:text-sm">
              {t('betHistory')} ({totalBetsCount})
            </TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-transactions" className="text-xs sm:text-sm">
              {t('transactions')} ({transactions.length})
            </TabsTrigger>
            <TabsTrigger value="settings" className="text-xs sm:text-sm">
              {language === 'de' ? 'Einstellungen' : 'Settings'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="settings" className="mt-4 sm:mt-6 space-y-6">
            <Card className="border-border/50 shadow-sm overflow-hidden">
              <CardHeader className="bg-secondary/10 border-b border-border/10">
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                  {language === 'de' ? 'Account Einstellungen' : 'Account Settings'}
                </CardTitle>
                <CardDescription>
                  {needsPaypal
                    ? (language === 'de' ? 'Verwalte deine Profilinformationen und PayPal-Links.' : 'Manage your profile information and PayPal links.')
                    : (language === 'de' ? 'Verwalte deine Profilinformationen.' : 'Manage your profile information.')}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  {/* Profilbild Upload */}
                  <div className="space-y-2 pb-4 border-b border-border/10">
                    <Label className="flex items-center gap-2">
                      <ImageIcon className="w-4 h-4 text-muted-foreground" />
                      {language === 'de' ? 'Profilbild' : 'Profile Picture'}
                    </Label>
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <div className="relative">
                        <Avatar className="h-16 w-16 shadow-inner border border-border">
                          <AvatarImage src={localAvatarPreviewUrl || avatarUrl} alt={user?.username} className="object-cover" />
                          <AvatarFallback className="bg-red-600/20 text-red-500 font-bold text-xl">
                            {user?.username?.[0]?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        {uploadingAvatar && (
                          <div className="absolute inset-0 bg-black/60 rounded-full flex items-center justify-center text-white z-10">
                            <Loader2 className="w-5 h-5 animate-spin" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 w-full sm:w-auto">
                        <Input 
                          type="file" 
                          accept="image/*" 
                          onChange={handleAvatarUpload} 
                          disabled={uploadingAvatar}
                          className="max-w-xs h-10 text-xs py-2"
                        />
                        <p className="text-[10px] text-muted-foreground italic">
                          {language === 'de' ? 'Unterstützt JPG, PNG, GIF, WEBP (max. 5MB)' : 'Supports JPG, PNG, GIF, WEBP (max. 5MB)'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      Email
                    </Label>
                    <Input value={user?.email} disabled className="bg-muted/50" />
                    <p className="text-[10px] text-muted-foreground italic flex items-center gap-1">
                      {user?.email_verified ? (
                        <><ShieldCheck className="w-3 h-3 text-green-500" /> {language === 'de' ? 'Verifiziert' : 'Verified'}</>
                      ) : (
                        <><ShieldAlert className="w-3 h-3 text-yellow-500" /> {language === 'de' ? 'Nicht verifiziert' : 'Not verified'}</>
                      )}
                    </p>
                  </div>

                  {needsPaypal && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2" htmlFor="paypal_link">
                      <LinkIcon className="w-4 h-4 text-muted-foreground" />
                      PayPal.me Link
                    </Label>
                    <Input
                      id="paypal_link"
                      placeholder="https://paypal.me/deinname"
                      value={paypalLink}
                      onChange={(e) => setPaypalLink(e.target.value)}
                      className="border-primary/20 focus:border-primary"
                    />
                    <p className="text-[10px] text-muted-foreground leading-tight italic">
                      {language === 'de'
                        ? 'Dieser Link wird Gewinnern angezeigt, wenn sie dich auszahlen müssen.'
                        : 'This link will be shown to winners when they need to pay you out.'}
                    </p>
                  </div>
                  )}

                  <Button type="submit" disabled={saving} className="w-full sm:w-auto gap-2">
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {language === 'de' ? 'Profil Speichern' : 'Save Profile'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bets" className="mt-4 sm:mt-6">
            {betsLoading && bets.length === 0 ? (
               <div className="space-y-3">
                 {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full" />)}
               </div>
            ) : bets.length > 0 ? (
              <div className="space-y-4">
                <Card className="border-border/50 overflow-hidden">
                  <CardContent className="p-0">
                    <div className={cn("divide-y divide-border/50", betsLoading && "opacity-50 pointer-events-none")}>
                      {bets.map(bet => (
                        <Link 
                          key={bet.id} 
                          to={`/market/${bet.market_id}`}
                          className="block hover:bg-secondary/30 transition-colors"
                        >
                          <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-sm sm:text-base truncate">{bet.market_title}</p>
                              <div className="flex items-center gap-2 mt-1 text-[10px] sm:text-xs text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                <span>{new Date(bet.created_at).toLocaleDateString(undefined)}</span>
                              </div>
                            </div>

                            <div className="flex items-center justify-between sm:justify-end gap-4 shrink-0">
                              <Badge className={cn("px-2 py-0.5 text-[10px] font-bold", bet.position === 'yes' 
                                ? 'bg-green-600/20 text-green-400 border-green-600/30' 
                                : 'bg-red-600/20 text-red-400 border-red-600/30'
                              )}>
                                {bet.position === 'yes' ? t('yes') : t('no')}
                              </Badge>

                              <div className="text-right flex flex-col items-end">
                                {bet.status === 'won' ? (
                                  <>
                                    <p className="font-mono font-bold text-sm text-green-400">
                                      +{Math.max(0, (bet.payout || 0) - (bet.amount || 0)).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                    </p>
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 mt-1 text-green-400 border-green-600/30 bg-green-600/5">
                                      {t(bet.status)}
                                    </Badge>
                                    <p className="text-[10px] text-muted-foreground mt-1">
                                      {language === 'de' ? 'Einsatz:' : 'Stake:'} {(bet.amount || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                    </p>
                                  </>
                                ) : bet.status === 'lost' ? (
                                  <>
                                    <p className="font-mono font-bold text-sm text-red-400">
                                      -{(bet.amount || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                    </p>
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 mt-1 text-red-400 border-red-600/30 bg-red-600/5">
                                      {t(bet.status)}
                                    </Badge>
                                    <p className="text-[10px] text-muted-foreground mt-1">
                                      {language === 'de' ? 'Einsatz:' : 'Stake:'} {(bet.amount || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                    </p>
                                  </>
                                ) : bet.status === 'refunded' ? (
                                  <>
                                    <p className="font-mono font-bold text-sm text-muted-foreground">
                                      0,00 €
                                    </p>
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 mt-1 text-muted-foreground border-border/30">
                                      {t(bet.status)}
                                    </Badge>
                                    <p className="text-[10px] text-muted-foreground mt-1">
                                      {language === 'de' ? 'Erstattet:' : 'Refunded:'} {(bet.amount || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                    </p>
                                  </>
                                ) : (
                                  <>
                                    <p className="font-mono font-bold text-sm text-yellow-500">
                                      {(bet.amount || 0).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                    </p>
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 mt-1 text-muted-foreground border-border/30">
                                      {t(bet.status)}
                                    </Badge>
                                    {bet.potential_payout > 0 && (
                                      <p className="text-[10px] text-green-400/70 font-medium mt-1">
                                        {language === 'de' ? 'Mögl. Gewinn: +' : 'Pot. Profit: +'}
                                        {Math.max(0, (bet.potential_payout || 0) - (bet.amount || 0)).toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                      </p>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 py-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1 || betsLoading}
                      className="gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      {language === 'de' ? 'Zurück' : 'Previous'}
                    </Button>
                    
                    <div className="flex items-center gap-1 mx-2">
                      <span className="text-sm font-medium">{currentPage}</span>
                      <span className="text-sm text-muted-foreground">/</span>
                      <span className="text-sm text-muted-foreground">{totalPages}</span>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages || betsLoading}
                      className="gap-1"
                    >
                      {language === 'de' ? 'Weiter' : 'Next'}
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <Card className="p-12 text-center border-border/50">
                <p className="text-muted-foreground">{t('noBetsYet')}</p>
              </Card>
            )}
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="mt-4 sm:mt-6">
            {transactions.length > 0 ? (
              <Card className="border-border/50 overflow-hidden">
                <CardContent className="p-0">
                  <div className="divide-y divide-border/50">
                    {transactions.map(tx => {
                      const isClickable = tx.reference_id && (tx.reference_type === 'bet' || tx.reference_type === 'market');
                      const content = (
                        <div className="p-4 flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={cn("p-2 rounded-xl shrink-0", 
                              tx.amount > 0 
                                ? 'bg-green-600/20 text-green-400 border border-green-600/30' 
                                : 'bg-red-600/20 text-red-400 border border-red-600/30'
                            )}>
                              {tx.amount > 0 
                                ? <ArrowUpRight className="w-4 h-4" />
                                : <ArrowDownRight className="w-4 h-4" />
                              }
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-sm capitalize">{t(tx.type) || tx.type}</p>
                              <p className="text-xs text-muted-foreground truncate max-w-[150px] sm:max-w-xs">
                                {tx.description}
                              </p>
                            </div>
                          </div>

                          <div className="text-right shrink-0">
                            <p className={cn("font-mono font-bold text-sm", 
                              tx.amount > 0 ? 'text-green-400' : 'text-red-400'
                            )}>
                              {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                            </p>
                            <p className="text-[10px] text-muted-foreground mt-1">
                              {new Date(tx.created_at).toLocaleDateString(undefined)}
                            </p>
                          </div>
                        </div>
                      );

                      if (isClickable) {
                        const path = tx.reference_type === 'bet' ? `/market/redirect?bet_id=${tx.reference_id}` : `/market/${tx.reference_id}`;
                        // Since we don't have a direct redirect, we just use market if available or best guess
                        // If it's a bet, we'd need to fetch the market_id for that bet, but we don't have it here.
                        // For now, let's keep it simple or only link markets.
                        return (
                          <div key={tx.id} className="block hover:bg-secondary/30 transition-colors">
                            {content}
                          </div>
                        );
                      }

                      return <div key={tx.id}>{content}</div>;
                    })}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="p-12 text-center border-border/50">
                <p className="text-muted-foreground">{t('noTransactions')}</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        <ImageCropperModal
          isOpen={isCropperOpen}
          onClose={() => setIsCropperOpen(false)}
          imageFile={selectedFileForCrop}
          circular={true}
          aspectRatio={1}
          onCrop={handleCroppedAvatarUpload}
          language={language}
        />
      </main>
    </div>
  );
};

export default Profile;
